<template>
  <div>
    <div class="vx-row">
      <div class="vx-col w-full">
        <div class="flex mt-2 mb-3 items-center">
          <router-link to="/manageRoles" class="mr-2">
            <feather-icon icon="ArrowLeftIcon" svgClasses="text-dark" />
          </router-link>
          <h3>{{ $t("system.label.permissions") }}</h3>
        </div>
      </div>

      <div class="vx-col w-full">
        <div class="flex justify-end gap-3 mb-3">
          <vs-button v-if="!edit" color="primary" icon-pack="feather" icon="icon-edit" type="border" size="small"
            @click.stop="editData">
            {{ $t("system.label.edit") }}
          </vs-button>

          <vs-button color="danger" icon-pack="feather" icon="icon-x" size="small" @click="deletePlanPopup = true">
            {{ $t("system.label.delete") }}
          </vs-button>
        </div>
      </div>

      <!-- Role Info -->
      <div class="vx-col w-full mb-4">
        <vx-card no-shadow>
          <table class="table w-full table-bordered table-striped">
            <tr>
              <td><b>{{ $t("system.label.name") }}</b></td>
              <td>{{ rolePermission.name }}</td>
            </tr>
            <tr>
              <td><b>{{ $t("system.label.description") }}</b></td>
              <td>{{ rolePermission.description }}</td>
            </tr>
          </table>
        </vx-card>
      </div>

      <!-- Permissions Section -->
      <div class="vx-col w-full">
        <vx-card no-shadow>
            <div>
              <div v-if="!edit">
                <div class="flex items-center mb-4">
                  <vs-input v-model="searchQuery" placeholder="Search all Permission" class="w-1/3">
                    <template #icon>
                      <feather-icon icon="SearchIcon" class="text-gray-500" />
                    </template>
                  </vs-input>
                </div>
                <div>
                  <div 
                    v-for="(group, index) in filteredGroupedPermissions" 
                    :key="index"
                    class=" rounded-lg mb-4 bg-white shadow-sm">
                    <div 
                      class="flex items-center px-4 py-3 border cursor-pointer permission-border"
                      @click="toggleCollapse(group.name)">
                      <h4 class="text-md font-bold">{{ group.name }}</h4>
                      <feather-icon 
                        :icon="collapsedGroups[group.name] ? 'ChevronDownIcon' : 'ChevronUpIcon'" 
                      />
                    </div>

                    <!-- Permissions -->
                    <transition name="fade">
                      <div 
                        v-show="!collapsedGroups[group.name]" 
                        class="bg-white"
                      >
                        <div 
                          v-for="permission in group.permissions" 
                          :key="permission.code"
                          class="flex items-center gap-3 px-4 py-2 permission-border"
                          :class="{ 'border-b-0': idx === group.permissions.length - 1 }">
                          <vs-switch 
                            v-model="permissions[permission.code]" 
                            disabled 
                          />
                          <span>{{ permission.code }}</span>
                        </div>
                      </div>
                    </transition>
                  </div>
                </div>
              </div>

              <div v-else>
                <div class="flex items-center mb-4">
                  <div class="dropdown-with-label">
                  <small 
                    class="date-label font-semibold">
                    {{ $t("system.label.roles") }}
                  </small>
                  <!-- <vs-select 
                    v-model="activeRole" 
                    class="w-full mr-5 select-input" 
                    placeholder="Select Role"
                    @input="setActive(activeRole)"
                  >
                    <vs-select-item 
                      v-for="(role, index) in roleList" :key="index" :value="role" :text="role" 
                    />
                  </vs-select> -->
                  <v-select
                    v-model="activeRole"
                    :options="roleList"
                    placeholder="Select Role"
                    class="min-w-200 w-full mr-5 select-style"
                    @input="setActive(activeRole)"
                  />
                  </div>
                  <vs-input 
                    v-model="searchQuery" 
                    placeholder="Search Permission" 
                    class="w-full search-input" 
                  />
                </div>
                <div class="items-center mt-4 mb-4">
                  <vs-checkbox v-model="selectAll" @change="toggleSelectAll">
                    {{ $t("Select All") }}
                  </vs-checkbox>
                </div>

                <!-- Permissions List -->
                <div v-for="(perm, index) in filteredPermissions" :key="index"
                  class="mb-3 p-3 rounded-lg permission-border">
                  <div class="flex items-center gap-4">
                    <vs-switch v-model="permissions[perm.code]" />
                    <span>{{ perm.code }}</span>
                  </div>
                </div>
              </div>
            </div>
        </vx-card>
      </div>

      <div class="vx-col w-full mt-6" v-if="edit">
        <div class="flex justify-center gap-6">
          <vs-button
            color="primary" 
            type="border" 
            class="footerButton-cancel" 
            @click="cancel"
          >
            {{ $t("system.label.cancel") }}
          </vs-button>
          <vs-button 
            class="footerButton-next" 
            @click="updatePermissionData"
          >
            {{ $t("system.label.submit") }}
          </vs-button>
        </div>
      </div>
    </div>

    <delete-popup 
      :deletePopup="deletePlanPopup" 
      :title="$t('system.label.Role')" 
      @deletePressed="deleteRole"
      @cancelPressed="deletePlanPopup = false" 
    />

    <validation-popup 
      :errorMessage="errorMessage" 
      :warningPopup="warningPopup" 
      :warningText="warningText"
      @closeWarning="warningPopup = false" 
    />
  </div>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";
import vSelect from "vue-select";

export default {
  data() {
    return {
      warningPopup: false,
      warningText: "",
      errorMessage: "",
      id: 0,
      entityId: 0,
      activeRole: null,
      deletePlanPopup: false,
      roleList: [],
      activePermissions: [],
      activeRole: null,
      edit: false,
      permissions: {},
      searchQuery: "",
      selectAll: false,
      collapsedGroups: {},
    };
  },
  components: {
    "v-select": vSelect,
  },

  computed: {
    ...mapState({
      rolePermission: (state) => state.manageRoles.permissionByRole,
    }),

    groupedPermissions() {
      const result = {};
      for (const perm of this.rolePermission.permissionUsageData || []) {
        if (perm.selected) {
          if (!result[perm.grouping]) result[perm.grouping] = [];
          result[perm.grouping].push(perm);
        }
      }
      return Object.keys(result).map((group) => ({
        name: group,
        permissions: result[group],
      }));
    },
    filteredGroupedPermissions() {
      const groups = this.groupedPermissions;
      if (!this.searchQuery) return groups;

      return groups
        .map((group) => {
          const filtered = group.permissions.filter((perm) =>
            perm.code.toLowerCase().includes(this.searchQuery.toLowerCase())
          );
          return { ...group, permissions: filtered };
        })
        .filter((group) => group.permissions.length > 0);
    },

    filteredPermissions() {
      if (!this.searchQuery) return this.activePermissions;
      return this.activePermissions.filter((perm) =>
        perm.code.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },
  },

  methods: {
    toggleCollapse(groupName) {
      this.$set(this.collapsedGroups, groupName, !this.collapsedGroups[groupName]);
    },
    async getPermissionsByRole() {
      try {
        await this.$store.dispatch("manageRoles/setPermissionsByRole", this.id);
        const dataList = this.rolePermission.permissionUsageData;

        const permissionsMap = {};
        this.roleList = [];

        for (const temp of dataList) {
          permissionsMap[temp.code] = temp.selected;
          if (this.roleList.indexOf(temp.grouping) === -1) {
            this.roleList.push(temp.grouping);
          }
        }

        this.permissions = { ...permissionsMap };

        // if (this.edit) {
        //   this.setActive(this.roleList[0] || null);
        // }
      } catch (error) {
        throw Error(
          (_context.t0.message &&
            _context.t0.message.errors &&
            _context.t0.message.errors[0] &&
            _context.t0.message.errors[0].defaultUserMessage) ||
          "Api failed with status code: " + (_context.t0.message && _context.t0.message.httpStatusCode)
        );
      }
    },

    async updatePermissionData() {
      try {
        await this.$store.dispatch("manageRoles/editRoleAction", {
          id: this.id,
          data: { permissions: this.permissions },
        });
        this.$swal({
          title: "Success",
          icon: "success",
          text: "Permissions Updated successfully!",
          type: "success",
        });
        this.edit = false;
        this.getPermissionsByRole();
      } catch (e) {
        throw Error(
          (_context.t0.message &&
            _context.t0.message.errors &&
            _context.t0.message.errors[0] &&
            _context.t0.message.errors[0].defaultUserMessage) ||
          "Api failed with status code: " + (_context.t0.message && _context.t0.message.httpStatusCode)
        );
      }
    },

    async deleteRole() {
      try {
        await this.$store.dispatch("manageRoles/deleteRoleAction", this.id);
        this.deletePlanPopup = false;
        await this.$swal({
          title: "Success",
          icon: "success",
          text: "Role Successfully Deleted!!",
          type: "success",
        });
        this.$router.push("/manageRoles");
      } catch (e) {
        let errorMsg = "Request was understood but caused a domain rule violation";
        if (e.response && e.response.data) {
          errorMsg = e.response.data.message || errorMsg;
        }
        if (e.graphQLErrors && e.graphQLErrors.length > 0) {
          errorMsg = e.graphQLErrors[0].message;
        }
        this.$swal({
          title: "Failed",
          icon: "warning",
          text: errorMsg,
          type: "warning",
        });
      }
    },

    editData() {
      this.edit = true;
      this.getPermissionsByRole();
    },

    cancel() {
      this.$router.push("/manageRoles");
    },

    setActive(role) {
      this.activeRole = role;
      this.activePermissions = this.rolePermission.permissionUsageData.filter(
        (temp) => temp.grouping === role
      );
    },

    toggleSelectAll() {
      this.activePermissions.forEach((perm) => {
        this.permissions[perm.code] = this.selectAll;
      });
    },
  },

  mounted() {
    if (this.$route.query.edit === "true") {
      this.edit = true;
    }
    this.id = this.$route.params.id;
    this.getPermissionsByRole();
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  max-height: 0;
  overflow: hidden;
}

.table {
  width: 100%;
  max-width: 100%;
  margin-bottom: 20px;
  background-color: #fff;
  border: solid 1px #f0f0f0;
}

.table td {
  vertical-align: middle;
  border-top: none;
  padding: 10px 7px;
  font-size: 14px;
  color: #646a71;
  border-bottom: 1px solid #dfe3e7;
  transition: all 0.2s ease;
  overflow: hidden;
  word-break: break-word;
}

.permission-border {
  border: 1px solid #EEE;
}
.search-input{
  max-width: 300px;
  border-radius: 40%;
  margin-top: -18px;
  margin-left: 10px;
}
.select-input{
  max-width: 400px;
  margin-top: 17px;
}
</style>
